import { DuckDBEngine, TableRef, TableRegistrationOptions } from '@/lib/engine';
import { trackAction } from './tracking';

/**
 * Enum for dataframe sources
 */
export enum DataframeSource {
  /** User uploaded a file through the UI */
  UPLOAD = 'upload',
  /** File opened from desktop app */
  OPEN_FROM_DESKTOP = 'open-from-desktop',
  /** Example dataset */
  EXAMPLE = 'example',
  /** File converted from another format */
  CONVERTED_FILE = 'converted-file'
}

/**
 * Parameters for registering a dataframe
 */
export interface RegisterDataframeParams {
  /** The file to register */
  file: File;
  /** Optional registration options */
  options?: TableRegistrationOptions;
  /** The engine instance to use */
  engine: DuckDBEngine;
  /** Source of the dataframe */
  source: DataframeSource;
}

/**
 * Registers a file with the DuckDB engine and sets it as the active table.
 * This is a common function used across the application for file registration.
 * 
 * @param params - Object containing registration parameters
 * @returns A promise that resolves to the registered table reference
 */
export async function registerDataframe(
  params: RegisterDataframeParams
): Promise<TableRef> {
  const { file, options, engine, source } = params;
  
  try {
    // Register the file with DuckDB engine and get the table reference
    const tableRef = await engine.registerFromFile(file, { 
      ...options,
      upsert: options?.upsert !== false // Default to upsert: true unless explicitly set to false
    });
    
    // Convert file size from bytes to MB (rounded to 2 decimal places)
    const fileSizeMB = Number((file.size / (1024 * 1024)).toFixed(2));
    
    // Extract file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
    
    // Track the dataframe creation event
    trackAction('dataframe created', {
      fileType: file.type || 'unknown',
      fileName: file.name,
      fileExtension,
      fileSizeMB,
      tableId: tableRef.id,
      tablePath: tableRef.path,
      source
    });
    
    // Return the table reference for further processing
    return tableRef;
  } catch (error) {
    console.error('Error registering dataframe:', error);
    throw error instanceof Error 
      ? error 
      : new Error('Failed to register dataframe');
  }
} 